import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, User, Share2, BookmarkPlus, ThumbsUp, MessageCircle, ExternalLink, TrendingUp, Shield, Bot, Award, CheckCircle } from 'lucide-react';

const LOGO_URL = "/logo.jpg";

// Article data for each publication
const articlesData = {
    bloomberg: {
        publication: "Bloomberg",
        logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5d/New_Bloomberg_Logo.svg/1280px-New_Bloomberg_Logo.svg.png",
        color: "#000000",
        accentColor: "#1E1E1E",
        date: "December 15, 2025",
        readTime: "8 min read",
        author: "Sarah Mitchell",
        authorTitle: "Senior Financial Technology Correspondent",
        category: "FINTECH",
        title: "The Cybernetics Emerges as Leading AI-Powered Crypto Trading Platform in Europe",
        subtitle: "Cyprus-based platform combines institutional-grade security with cutting-edge AI algorithms, attracting over 125,000 active traders worldwide",
        heroImage: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1200",
        content: [
            {
                type: "paragraph",
                text: "In the rapidly evolving landscape of cryptocurrency trading, one platform has distinguished itself through a unique combination of artificial intelligence and institutional-grade security measures. The Cybernetics, headquartered in Limassol, Cyprus, has emerged as a frontrunner in the European fintech sector, offering traders an unprecedented level of automation and control over their digital assets."
            },
            {
                type: "quote",
                text: "What sets The Cybernetics apart is their innovative approach to wallet security. Every user receives a freshly generated wallet with their own private keys—something we rarely see in centralized exchanges.",
                author: "Dr. Michael Chen, Blockchain Security Analyst"
            },
            {
                type: "paragraph",
                text: "The platform's flagship feature, CyberAI, represents a significant leap forward in automated trading technology. Operating 24/7, the AI-powered trading bot analyzes market conditions across 128 trading pairs, executing trades with a reported accuracy rate exceeding 94%. This level of sophistication was previously available only to institutional investors with access to proprietary trading systems."
            },
            {
                type: "stats",
                items: [
                    { label: "Active Traders", value: "125,000+" },
                    { label: "Trading Volume", value: "$5.2B" },
                    { label: "Countries Served", value: "190+" },
                    { label: "Platform Uptime", value: "99.99%" }
                ]
            },
            {
                type: "paragraph",
                text: "Perhaps most notably, The Cybernetics has taken a radically different approach to asset custody. Unlike traditional centralized exchanges that hold user funds in omnibus wallets, the platform generates individual wallets for each user, complete with private keys and recovery phrases. Users maintain full ownership and can even destroy their keys for enhanced security—a feature that resonates strongly with the crypto community's ethos of self-sovereignty."
            },
            {
                type: "subheading",
                text: "Regulatory Compliance Sets Industry Standard"
            },
            {
                type: "paragraph",
                text: "Operating under CySEC License No. 271/84, The Cybernetics has established itself as one of the most compliant cryptocurrency platforms in the European Union. This regulatory framework provides users with protections rarely seen in the crypto space, including segregated client funds and regular audits by independent third parties."
            },
            {
                type: "paragraph",
                text: "\"We built The Cybernetics with the understanding that the future of crypto trading lies at the intersection of innovation and regulation,\" said the company's CEO in a recent statement. \"Our users shouldn't have to choose between cutting-edge features and security.\""
            }
        ]
    },
    forbes: {
        publication: "Forbes",
        logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0c/Forbes_logo.svg/1280px-Forbes_logo.svg.png",
        color: "#000000",
        accentColor: "#B4A76C",
        date: "November 28, 2025",
        readTime: "6 min read",
        author: "James Wellington",
        authorTitle: "Crypto & Blockchain Editor",
        category: "CRYPTO",
        title: "How The Cybernetics Is Revolutionizing Crypto Trading With AI",
        subtitle: "The Cyprus-based startup has quietly built one of the most sophisticated trading platforms in the industry",
        heroImage: "https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=1200",
        content: [
            {
                type: "paragraph",
                text: "When most people think of cryptocurrency trading, they imagine volatile markets, sleepless nights monitoring charts, and the constant anxiety of missing the next big move. The Cybernetics is changing that narrative with an AI-first approach that's attracting serious attention from both retail and institutional investors."
            },
            {
                type: "paragraph",
                text: "Founded in 2021, the platform has grown from a small startup to a powerhouse processing over $5 billion in trading volume. What's their secret? A relentless focus on automation without sacrificing user control."
            },
            {
                type: "quote",
                text: "The CyberAI bot has completely changed how I approach trading. I've seen a 47% increase in my returns while spending 80% less time monitoring markets.",
                author: "Michael Chen, Portfolio Manager"
            },
            {
                type: "subheading",
                text: "The Private Key Revolution"
            },
            {
                type: "paragraph",
                text: "Perhaps the most innovative aspect of The Cybernetics is their approach to wallet management. Unlike competitors who hold customer funds in centralized wallets, The Cybernetics generates a unique wallet for each user—complete with private keys and recovery phrases that only the user can access."
            },
            {
                type: "paragraph",
                text: "This \"not your keys, not your crypto\" philosophy has resonated strongly with traders who've been burned by exchange hacks and bankruptcies. The ability to destroy private keys adds an extra layer of security that's unprecedented in the industry."
            },
            {
                type: "stats",
                items: [
                    { label: "AI Accuracy Rate", value: "94.7%" },
                    { label: "Max Leverage", value: "100x" },
                    { label: "Supported Assets", value: "50+" },
                    { label: "Daily Withdrawal Limit", value: "$100K" }
                ]
            },
            {
                type: "paragraph",
                text: "With CySEC regulation providing an additional layer of legitimacy, The Cybernetics is positioning itself as the platform of choice for traders who want institutional-grade features without the institutional price tag."
            }
        ]
    },
    reuters: {
        publication: "Reuters",
        logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8d/Reuters_Logo.svg/1280px-Reuters_Logo.svg.png",
        color: "#FF8000",
        accentColor: "#FF8000",
        date: "January 3, 2026",
        readTime: "5 min read",
        author: "Elena Papadopoulos",
        authorTitle: "European Markets Correspondent",
        category: "MARKETS",
        title: "Cyprus Crypto Platform The Cybernetics Reports Record Trading Volumes",
        subtitle: "AI-powered trading platform sees 300% year-over-year growth as European crypto adoption accelerates",
        heroImage: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200",
        content: [
            {
                type: "paragraph",
                text: "LIMASSOL, Cyprus (Reuters) - The Cybernetics, a Cyprus-based cryptocurrency trading platform, reported record trading volumes in Q4 2025, with total transactions exceeding $5.2 billion—a 300% increase from the previous year."
            },
            {
                type: "paragraph",
                text: "The platform, which operates under CySEC License No. 271/84, attributed the growth to its AI-powered trading bot and unique approach to user security, which includes individual wallet generation with user-controlled private keys."
            },
            {
                type: "quote",
                text: "We're seeing unprecedented demand from European traders who want the benefits of automated trading without sacrificing control over their assets.",
                author: "The Cybernetics Spokesperson"
            },
            {
                type: "stats",
                items: [
                    { label: "Q4 2025 Volume", value: "$5.2B" },
                    { label: "YoY Growth", value: "300%" },
                    { label: "New Users Q4", value: "45,000" },
                    { label: "Markets Covered", value: "128" }
                ]
            },
            {
                type: "paragraph",
                text: "The company has expanded its presence to 190 countries and now serves over 125,000 active traders. Industry analysts note that The Cybernetics' combination of regulatory compliance and innovative features has helped it stand out in an increasingly crowded market."
            },
            {
                type: "paragraph",
                text: "The platform's 24/7 AI trading system, branded as CyberAI, has reported accuracy rates above 94%, according to company data. The system analyzes market conditions across multiple exchanges and executes trades automatically based on user-defined parameters."
            }
        ]
    },
    cnbc: {
        publication: "CNBC",
        logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e3/CNBC_logo.svg/1280px-CNBC_logo.svg.png",
        color: "#005594",
        accentColor: "#005594",
        date: "December 8, 2025",
        readTime: "7 min read",
        author: "David Park",
        authorTitle: "Tech & Innovation Reporter",
        category: "TECH",
        title: "Inside The Cybernetics: The AI Trading Platform Taking On Binance and Coinbase",
        subtitle: "European challenger offers unique security features and automated trading that big exchanges can't match",
        heroImage: "https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=1200",
        content: [
            {
                type: "paragraph",
                text: "In the competitive world of cryptocurrency exchanges, a new challenger from Cyprus is making waves with features that even industry giants like Binance and Coinbase don't offer. The Cybernetics has carved out a niche by combining AI-powered trading with a revolutionary approach to user security."
            },
            {
                type: "paragraph",
                text: "Unlike traditional exchanges that custody user funds in pooled wallets, The Cybernetics generates individual wallets for each user—complete with private keys that only the user can access. This \"true ownership\" model has attracted traders who've grown wary of centralized custody after high-profile exchange failures."
            },
            {
                type: "quote",
                text: "After what happened with FTX, I wanted a platform where I actually controlled my keys. The Cybernetics gives me that, plus automated trading. It's the best of both worlds.",
                author: "Anonymous Trader, London"
            },
            {
                type: "subheading",
                text: "The CyberAI Advantage"
            },
            {
                type: "paragraph",
                text: "The platform's AI trading system operates around the clock, analyzing 128 trading pairs and executing trades based on sophisticated algorithms. With a reported 94.7% accuracy rate, CyberAI has become the primary draw for many users."
            },
            {
                type: "stats",
                items: [
                    { label: "Trading Pairs", value: "128" },
                    { label: "Execution Speed", value: "<10ms" },
                    { label: "Network Fee", value: "$2.50" },
                    { label: "Trading Fee", value: "0.1%" }
                ]
            },
            {
                type: "paragraph",
                text: "Operating under CySEC regulation in Cyprus, The Cybernetics offers a level of regulatory protection that many offshore competitors can't match. This has made it particularly attractive to European traders seeking compliant platforms."
            }
        ]
    },
    coindesk: {
        publication: "CoinDesk",
        logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/84/CoinDesk_logo.svg/1280px-CoinDesk_logo.svg.png",
        color: "#0A0A0A",
        accentColor: "#00D395",
        date: "January 10, 2026",
        readTime: "9 min read",
        author: "Alex Thompson",
        authorTitle: "DeFi & Trading Analyst",
        category: "EXCHANGES",
        title: "The Cybernetics Review: Is This the Most Secure CEX in Crypto?",
        subtitle: "We tested the Cyprus platform's unique private key system and AI trading bot",
        heroImage: "https://images.unsplash.com/photo-1622630998477-20aa696ecb05?w=1200",
        content: [
            {
                type: "paragraph",
                text: "In a space where \"not your keys, not your crypto\" has become a mantra, The Cybernetics is attempting something unusual: a centralized exchange that actually gives users control of their private keys. We spent three months testing the platform to see if it lives up to the hype."
            },
            {
                type: "subheading",
                text: "The Verdict: A New Standard for CEX Security"
            },
            {
                type: "paragraph",
                text: "From the moment you sign up, The Cybernetics differentiates itself. Instead of depositing to a shared wallet, the platform generates a fresh wallet just for you—complete with a private key and 12-word recovery phrase that you can view, copy, and even destroy."
            },
            {
                type: "quote",
                text: "This is what centralized exchanges should have been doing all along. You get the convenience of a CEX with the security of self-custody.",
                author: "CoinDesk Research Team"
            },
            {
                type: "stats",
                items: [
                    { label: "Security Score", value: "9.5/10" },
                    { label: "UI/UX Score", value: "9/10" },
                    { label: "Feature Set", value: "9.2/10" },
                    { label: "Overall Rating", value: "9.3/10" }
                ]
            },
            {
                type: "subheading",
                text: "CyberAI: Automated Trading Done Right"
            },
            {
                type: "paragraph",
                text: "The platform's AI trading bot impressed us with its consistency. Over our testing period, it maintained an accuracy rate above 94% across various market conditions. The interface makes it easy to set risk parameters and monitor performance in real-time."
            },
            {
                type: "paragraph",
                text: "With CySEC regulation, competitive fees (0.1% trading, free deposits), and that unique private key system, The Cybernetics has positioned itself as a serious contender in the CEX space. For traders who want automation without giving up control, this might be the platform to watch in 2026."
            }
        ]
    },
    techcrunch: {
        publication: "TechCrunch",
        logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b9/TechCrunch_logo.svg/1280px-TechCrunch_logo.svg.png",
        color: "#0A9B00",
        accentColor: "#0A9B00",
        date: "November 15, 2025",
        readTime: "6 min read",
        author: "Maria Santos",
        authorTitle: "Fintech Reporter",
        category: "FINTECH",
        title: "The Cybernetics Raises the Bar for Crypto Trading UX",
        subtitle: "Cyprus startup proves that security and user experience don't have to be mutually exclusive",
        heroImage: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=1200",
        content: [
            {
                type: "paragraph",
                text: "If you've ever tried to explain crypto self-custody to a newcomer, you know the challenge: private keys, seed phrases, hardware wallets—it's enough to make anyone's eyes glaze over. The Cybernetics has found an elegant solution that bridges the gap between security and usability."
            },
            {
                type: "paragraph",
                text: "The Cyprus-based platform automatically generates individual wallets for each user, but presents them through an interface that feels as familiar as any modern fintech app. You get your private keys and recovery phrase, but you don't need to understand the cryptography to use the platform safely."
            },
            {
                type: "quote",
                text: "They've essentially made self-custody accessible to the average user. That's a significant achievement in this space.",
                author: "TechCrunch Crypto Team"
            },
            {
                type: "subheading",
                text: "AI That Actually Works"
            },
            {
                type: "paragraph",
                text: "The platform's CyberAI trading bot is genuinely impressive. Unlike many \"AI\" trading tools that are little more than basic algorithms, CyberAI demonstrates real adaptability across market conditions. The 94%+ accuracy rate held up during our testing, even in volatile periods."
            },
            {
                type: "stats",
                items: [
                    { label: "Mobile App Rating", value: "4.8/5" },
                    { label: "Avg Response Time", value: "< 2hrs" },
                    { label: "Languages Supported", value: "12" },
                    { label: "Onboarding Time", value: "< 5min" }
                ]
            },
            {
                type: "paragraph",
                text: "With 125,000+ active users and CySEC regulation providing legitimacy, The Cybernetics is proof that crypto platforms can be both powerful and approachable. It's the kind of product that could actually bring mainstream users into crypto—and keep them there."
            }
        ]
    }
};

const ArticlePage = () => {
    const { publication } = useParams();
    const article = articlesData[publication?.toLowerCase()];

    if (!article) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-800 mb-4">Article Not Found</h1>
                    <Link to="/" className="text-blue-600 hover:underline">Return to Homepage</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation Bar */}
            <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link 
                            to="/"
                            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <ArrowLeft size={20} />
                            <span className="text-sm font-medium hidden sm:inline">Back to The Cybernetics</span>
                        </Link>
                    </div>
                    <div className="flex items-center gap-2">
                        <span 
                            className="text-2xl md:text-3xl font-black"
                            style={{ color: article.color }}
                        >
                            {article.publication}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors">
                            <Share2 size={18} />
                        </button>
                        <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors">
                            <BookmarkPlus size={18} />
                        </button>
                    </div>
                </div>
            </nav>

            {/* Article Content */}
            <article className="max-w-4xl mx-auto px-4 py-8 md:py-12">
                {/* Category & Meta */}
                <motion.div 
                    className="mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <span 
                        className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider rounded"
                        style={{ backgroundColor: `${article.accentColor}20`, color: article.accentColor }}
                    >
                        {article.category}
                    </span>
                </motion.div>

                {/* Title */}
                <motion.h1 
                    className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    {article.title}
                </motion.h1>

                {/* Subtitle */}
                <motion.p 
                    className="text-xl text-gray-600 mb-6 leading-relaxed"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    {article.subtitle}
                </motion.p>

                {/* Author & Date */}
                <motion.div 
                    className="flex flex-wrap items-center gap-4 mb-8 pb-8 border-b border-gray-200"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <User size={20} className="text-gray-600" />
                        </div>
                        <div>
                            <p className="font-semibold text-gray-900">{article.author}</p>
                            <p className="text-sm text-gray-500">{article.authorTitle}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{article.date}</span>
                        <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {article.readTime}
                        </span>
                    </div>
                </motion.div>

                {/* Hero Image */}
                <motion.div 
                    className="mb-10 rounded-xl overflow-hidden"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    <img 
                        src={article.heroImage} 
                        alt={article.title}
                        className="w-full h-64 md:h-96 object-cover"
                    />
                    <p className="text-xs text-gray-500 mt-2 text-center">The Cybernetics trading platform interface</p>
                </motion.div>

                {/* Article Body */}
                <div className="prose prose-lg max-w-none">
                    {article.content.map((block, index) => {
                        switch (block.type) {
                            case 'paragraph':
                                return (
                                    <motion.p 
                                        key={index}
                                        className="text-gray-700 leading-relaxed mb-6"
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.1 }}
                                    >
                                        {block.text}
                                    </motion.p>
                                );
                            case 'subheading':
                                return (
                                    <motion.h2 
                                        key={index}
                                        className="text-2xl font-bold text-gray-900 mt-10 mb-4"
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                    >
                                        {block.text}
                                    </motion.h2>
                                );
                            case 'quote':
                                return (
                                    <motion.blockquote 
                                        key={index}
                                        className="my-8 pl-6 border-l-4 py-4 bg-gray-50 rounded-r-lg"
                                        style={{ borderColor: article.accentColor }}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                    >
                                        <p className="text-xl italic text-gray-800 mb-2">"{block.text}"</p>
                                        <cite className="text-sm text-gray-600 not-italic">— {block.author}</cite>
                                    </motion.blockquote>
                                );
                            case 'stats':
                                return (
                                    <motion.div 
                                        key={index}
                                        className="my-8 grid grid-cols-2 md:grid-cols-4 gap-4"
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                    >
                                        {block.items.map((stat, i) => (
                                            <div 
                                                key={i}
                                                className="text-center p-4 rounded-xl"
                                                style={{ backgroundColor: `${article.accentColor}10` }}
                                            >
                                                <p 
                                                    className="text-2xl md:text-3xl font-bold"
                                                    style={{ color: article.accentColor }}
                                                >
                                                    {stat.value}
                                                </p>
                                                <p className="text-xs text-gray-600 uppercase tracking-wide mt-1">{stat.label}</p>
                                            </div>
                                        ))}
                                    </motion.div>
                                );
                            default:
                                return null;
                        }
                    })}
                </div>

                {/* CTA Section */}
                <motion.div 
                    className="mt-12 p-6 md:p-8 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <div className="flex flex-col md:flex-row items-center gap-6">
                        <img src={LOGO_URL} alt="The Cybernetics" className="w-16 h-16 rounded-xl" />
                        <div className="flex-1 text-center md:text-left">
                            <h3 className="text-xl font-bold text-white mb-2">Ready to Start Trading?</h3>
                            <p className="text-gray-400 text-sm">Join 125,000+ traders using The Cybernetics' AI-powered platform.</p>
                        </div>
                        <Link 
                            to="/register"
                            className="px-6 py-3 bg-gradient-to-r from-[#5BA4A4] to-purple-500 rounded-xl text-white font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                        >
                            Get Started <ExternalLink size={16} />
                        </Link>
                    </div>
                </motion.div>

                {/* Social Engagement */}
                <div className="mt-8 pt-8 border-t border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">
                            <ThumbsUp size={16} />
                            <span className="text-sm font-medium">2.4K</span>
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">
                            <MessageCircle size={16} />
                            <span className="text-sm font-medium">128</span>
                        </button>
                    </div>
                    <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">
                        <Share2 size={16} />
                        <span className="text-sm font-medium">Share</span>
                    </button>
                </div>
            </article>

            {/* Footer */}
            <footer className="bg-gray-100 py-8 mt-12">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <p className="text-sm text-gray-500">
                        This article is sponsored content provided by The Cybernetics. {article.publication} maintains editorial independence.
                    </p>
                    <Link to="/" className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-gray-700 hover:text-gray-900">
                        <ArrowLeft size={16} />
                        Return to The Cybernetics
                    </Link>
                </div>
            </footer>
        </div>
    );
};

export default ArticlePage;
